/**
 * AUDIT LOG SERVICE - Re-export from Shared Core
 * 
 * Mantiene compatibilidad con imports existentes.
 * La implementación real está en @boosting/core.
 */

import { db } from '@/core/config/firebase';
import {
  AuditLogService as CoreAuditLogService,
  createAuditLogService,
  type AuditLogEntry,
  type CreateAuditLogDTO,
  type AuditAction,
} from '@boosting/core';

// Re-export types
export type { AuditLogEntry, CreateAuditLogDTO, AuditAction };

// Appmor-specific resource types
export type AuditResourceType =
  | 'session_note'
  | 'patient_data'
  | 'patient_mood'
  | 'patient_message'
  | 'practice_assignment'
  | 'patient_profile'
  | 'therapist_account'
  | 'therapist_note'
  | 'therapist_notes'
  | 'genogram'
  | 'consent'
  | 'user_account'
  | 'session'
  | 'user'
  | 'system_migration'
  | 'device'
  | 'patient_invite'
  | 'patient_journal'
  | 'therapeutic_tool'
  | 'tool_request'
  | 'patient_tool'
  | 'patient_tools'
  | 'wheel_of_life'
  | 'practice_library';

// Extended DTO for Appmor
export interface AppmorAuditLogDTO extends Omit<CreateAuditLogDTO, 'resourceType'> {
  resourceType: AuditResourceType;
  metadata?: Record<string, any>;
}

// Create singleton instance with Appmor's Firestore
const coreService = createAuditLogService(db as any, 'audit_logs');

// Extended service class for Appmor-specific methods
class AppmorAuditLogService {
  /**
   * Registrar una entrada de auditoría
   */
  async log(data: AppmorAuditLogDTO): Promise<void> {
    return coreService.log(data);
  }

  /**
   * Registrar visualización de datos sensibles
   */
  async logView(
    userId: string,
    userRole: string,
    resourceType: AuditResourceType,
    resourceId: string,
    details?: Record<string, unknown>
  ): Promise<void> {
    return coreService.logView(userId, userRole, resourceType, resourceId, details);
  }

  /**
   * Registrar modificación de datos
   */
  async logUpdate(
    userId: string,
    userRole: string,
    resourceType: AuditResourceType,
    resourceId: string,
    changes: Record<string, unknown>
  ): Promise<void> {
    return coreService.logUpdate(userId, userRole, resourceType, resourceId, changes);
  }

  /**
   * Registrar acceso denegado
   */
  async logAccessDenied(
    userId: string,
    userRole: string,
    resourceType: AuditResourceType,
    resourceId: string,
    reason: string
  ): Promise<void> {
    return coreService.logAccessDenied(userId, userRole, resourceType, resourceId, reason);
  }

  /**
   * Obtener logs por usuario
   */
  async getByUser(userId: string, limit = 100): Promise<AuditLogEntry[]> {
    return coreService.getByUser(userId, limit);
  }

  /**
   * Obtener logs por recurso
   */
  async getByResource(
    resourceType: AuditResourceType,
    resourceId: string,
    limit = 50
  ): Promise<AuditLogEntry[]> {
    return coreService.getByResource(resourceType, resourceId, limit);
  }

  /**
   * Obtener logs de accesos denegados
   */
  async getAccessDeniedLogs(limit = 100): Promise<AuditLogEntry[]> {
    return coreService.getAccessDeniedLogs(limit);
  }
}

export const auditLogService = new AppmorAuditLogService();
